/**
 * @module attackTechniques
 * @purpose Presentation-only MITRE ATT&CK technique id -> human-readable name lookup, bundled
 *   client-side so reference-match chips can render "T1095 — Non-Application Layer Protocol"
 *   without a round trip. Mirrors the backend's vendored catalogue verbatim (see
 *   backend/crates/evidenx-core/src/services/reasoning/data/references/attack_techniques.json
 *   and .claude/PRPs/reference/layer4-hypothesis/references/attack_techniques.json).
 * @depends none
 * @dependedOnBy HypothesisReferenceMatches
 *
 * @rules
 * - Performance: plain object lookup — no runtime parsing
 * - State: none
 * - Errors: n/a — techniqueName falls back to the raw id for unknown techniques
 */

/**
 * MITRE ATT&CK (Enterprise) technique id -> human-readable name.
 *
 * @behaviour Covers the techniques referenced by the current predicates and reference lists
 *   only, not the full ATT&CK matrix. Names are from MITRE ATT&CK; confirm against the current
 *   ATT&CK release before publishing.
 */
export const ATTACK_TECHNIQUE_NAMES: Record<string, string> = {
  T1003: 'OS Credential Dumping',
  'T1003.001': 'OS Credential Dumping: LSASS Memory',
  'T1003.002': 'OS Credential Dumping: Security Account Manager',
  'T1003.006': 'OS Credential Dumping: DCSync',
  T1021: 'Remote Services',
  'T1021.001': 'Remote Services: Remote Desktop Protocol',
  'T1021.002': 'Remote Services: SMB/Windows Admin Shares',
  T1046: 'Network Service Discovery',
  T1047: 'Windows Management Instrumentation',
  T1048: 'Exfiltration Over Alternative Protocol',
  'T1069.002': 'Permission Groups Discovery: Domain Groups',
  'T1070.001': 'Indicator Removal: Clear Windows Event Logs',
  'T1070.006': 'Indicator Removal: Timestomp',
  T1071: 'Application Layer Protocol',
  T1078: 'Valid Accounts',
  'T1078.002': 'Valid Accounts: Domain Accounts',
  T1087: 'Account Discovery',
  'T1087.002': 'Account Discovery: Domain Account',
  T1095: 'Non-Application Layer Protocol',
  T1105: 'Ingress Tool Transfer',
  T1110: 'Brute Force',
  T1219: 'Remote Access Software',
  T1482: 'Domain Trust Discovery',
  T1550: 'Use Alternate Authentication Material',
  'T1550.002': 'Use Alternate Authentication Material: Pass the Hash',
  'T1550.003': 'Use Alternate Authentication Material: Pass the Ticket',
  T1555: 'Credentials from Password Stores',
  'T1558.003': 'Steal or Forge Kerberos Tickets: Kerberoasting',
  'T1562.001': 'Impair Defenses: Disable or Modify Tools',
  'T1567.002': 'Exfiltration to Cloud Storage',
  'T1569.002': 'System Services: Service Execution',
  T1570: 'Lateral Tool Transfer',
  T1571: 'Non-Standard Port',
  'T1588.002': 'Obtain Capabilities: Tool',
}

/**
 * Resolve a MITRE ATT&CK technique id to its human-readable name.
 *
 * @behaviour Returns the raw id unchanged when the id is not in the bundled lookup, so a chip
 *   never renders blank for a technique the catalogue has not caught up with yet.
 * @errors n/a
 */
export function techniqueName(id: string): string {
  return ATTACK_TECHNIQUE_NAMES[id] ?? id
}
